/**
 * Formulario de "Novo Credenciado" — portado do protótipo (`CredenciadoForm.tsx`),
 * com os campos que a tabela `credenciados` de fato tem (db/migrations/0002).
 *
 * O QUE FICOU DE FORA, DE PROPÓSITO:
 * - Busca automática de CPF/CNPJ: o protótipo chamava uma função mock de
 *   Receita Federal (`mock-serpro`). Simular dado oficial de verdade é
 *   exatamente o que CLAUDE.md, "Proibido", recusa — o campo aqui é só
 *   digitado, com máscara e validação de dígito verificador.
 * - `DocumentChecklist`: pertence a um módulo de documentos/OCR que não existe
 *   neste repositório.
 *
 * O "Salvar" NÃO CHAMA API NENHUMA — nenhuma tabela cadastral aceita escrita
 * hoje (docs/ALVOS.md, Onda 2; ver `comum/cadastroIndisponivel.ts`). Ele só
 * mostra o aviso e mantém os dados no formulário.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, Landmark, MapPin, MessageSquare } from 'lucide-react';
import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  AVISO_DE_CADASTRO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import {
  mascaraCep,
  mascaraCnpj,
  mascaraCpf,
  mascaraTelefone,
  somenteDigitos,
} from '@/nucleo/formato/documentos';
import { useAutocompletarCep } from '@/nucleo/ganchos/useAutocompletarCep';
import {
  carregarRascunho,
  limparRascunho,
  useRascunhoDeFormulario,
} from '@/nucleo/ganchos/useRascunhoDeFormulario';
import { validarCpfCnpj } from '@/nucleo/validarDocumento';
import { AvisoDeRascunho } from '@/ui/AvisoDeRascunho';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/ui/base/form';
import { Input } from '@/ui/base/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Textarea } from '@/ui/base/textarea';
import { FormSection } from '@/ui/FormSection';
import { RodapeDoFormularioEmModal } from '@/ui/forms/RodapeDoFormularioEmModal';

import { OPCOES_DE_TIPO } from './contrato';

const CHAVE_DO_RASCUNHO = 'credenciado-form';

const REGIMES_TRIBUTARIOS = ['SIMPLES', 'LUCRO_PRESUMIDO', 'LUCRO_REAL', 'MEI', 'ISENTO'] as const;
const ROTULO_DO_REGIME: Record<(typeof REGIMES_TRIBUTARIOS)[number], string> = {
  SIMPLES: 'Simples Nacional (isento ISS retido)',
  LUCRO_PRESUMIDO: 'Lucro Presumido (5% ISS)',
  LUCRO_REAL: 'Lucro Real (5% ISS)',
  MEI: 'MEI (isento ISS retido)',
  ISENTO: 'Isento',
};

const esquema = z
  .object({
    tipo: z.enum(['PF', 'PJ', 'COOPERATIVA']),
    cpf_cnpj: z.string().min(1, 'CPF/CNPJ obrigatório'),
    nome: z.string().trim().min(2, 'Nome deve ter pelo menos 2 caracteres').max(200),
    nome_fantasia: z.string().max(200).optional(),
    termo_permissionario: z.string().max(50).optional(),
    email: z.string().trim().email('E-mail inválido').max(255).optional().or(z.literal('')),
    telefone: z.string().max(15).optional(),
    telefone2: z.string().max(15).optional(),
    cep: z.string().max(9).optional(),
    endereco: z.string().max(300).optional(),
    numero: z.string().max(20).optional(),
    complemento: z.string().max(100).optional(),
    bairro: z.string().max(100).optional(),
    cidade: z.string().max(100).optional(),
    uf: z.string().max(2).optional(),
    conta_banco: z.string().max(50).optional(),
    conta_agencia: z.string().max(20).optional(),
    conta_numero: z.string().max(20).optional(),
    conta_operacao: z.string().max(10).optional(),
    regime_tributario: z.enum(REGIMES_TRIBUTARIOS).optional().or(z.literal('')),
    observacoes: z.string().max(1000).optional(),
  })
  .superRefine((valores, ctx) => {
    if (!validarCpfCnpj(valores.cpf_cnpj, valores.tipo === 'PF' ? 'PF' : 'PJ')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cpf_cnpj'],
        message: valores.tipo === 'PF' ? 'CPF inválido' : 'CNPJ inválido',
      });
    }
  });

export type ValoresDoFormulario = z.infer<typeof esquema>;

const VALORES_INICIAIS: ValoresDoFormulario = {
  tipo: 'PF',
  cpf_cnpj: '',
  nome: '',
  nome_fantasia: '',
  termo_permissionario: '',
  email: '',
  telefone: '',
  telefone2: '',
  cep: '',
  endereco: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
  conta_banco: '',
  conta_agencia: '',
  conta_numero: '',
  conta_operacao: '',
  regime_tributario: '',
  observacoes: '',
};

export interface CredenciadoFormularioProps {
  onCancelar: () => void;
  /**
   * Pré-preenche o formulário com os dados de um credenciado existente —
   * usado pela tela de edição (`CredenciadoEditarPage`). Sem isto, o
   * formulário abre em branco (caso de "Novo Credenciado").
   */
  valoresIniciais?: Partial<ValoresDoFormulario>;
  /**
   * `true` quando este formulário roda numa PÁGINA inteira
   * (`CredenciadoEditarPage`), sem `ResponsiveModal` por perto para prover o
   * rodapé fixo — sem isto o rodapé (Cancelar/Salvar) não apareceria. Default
   * `false`: mantém o comportamento de "Novo Credenciado" (dentro de
   * `ResponsiveModal`) inalterado.
   */
  rodapeInline?: boolean;
}

export function CredenciadoFormulario({
  onCancelar,
  valoresIniciais,
  rodapeInline = false,
}: CredenciadoFormularioProps) {
  const idBase = useId();
  const formId = `credenciado-formulario-${idBase.replace(/:/g, '')}`;

  const [mostrarAvisoDeRascunho, setMostrarAvisoDeRascunho] = useState(
    () => !valoresIniciais && carregarRascunho<ValoresDoFormulario>(CHAVE_DO_RASCUNHO) !== null,
  );

  const form = useForm<ValoresDoFormulario>({
    resolver: zodResolver(esquema),
    defaultValues: { ...VALORES_INICIAIS, ...valoresIniciais },
    mode: 'onSubmit',
  });

  const valores = form.watch();
  const tipo = form.watch('tipo');
  const gravadoEm = useRascunhoDeFormulario(CHAVE_DO_RASCUNHO, valores, true);

  const {
    aoSairDoCampo: aoSairDoCep,
    carregando: carregandoCep,
    mensagemDeErro: erroDoCep,
  } = useAutocompletarCep((endereco) => {
    if (!form.getValues('endereco')) form.setValue('endereco', endereco.logradouro);
    if (!form.getValues('bairro')) form.setValue('bairro', endereco.bairro);
    if (!form.getValues('cidade')) form.setValue('cidade', endereco.cidade);
    if (!form.getValues('uf')) form.setValue('uf', endereco.uf);
  });

  function restaurarRascunho() {
    const rascunho = carregarRascunho<ValoresDoFormulario>(CHAVE_DO_RASCUNHO);
    if (rascunho) {
      form.reset(rascunho);
    }
    setMostrarAvisoDeRascunho(false);
  }

  function descartarRascunho() {
    limparRascunho(CHAVE_DO_RASCUNHO);
    setMostrarAvisoDeRascunho(false);
  }

  function enviar() {
    mostrarAviso(AVISO_DE_CADASTRO_INDISPONIVEL);
  }

  return (
    <Form {...form}>
      <form id={formId} onSubmit={form.handleSubmit(enviar)} className="space-y-6" noValidate>
        {mostrarAvisoDeRascunho && (
          <AvisoDeRascunho
            gravadoEm={gravadoEm}
            aoRestaurar={restaurarRascunho}
            aoDescartar={descartarRascunho}
          />
        )}

        <FormSection
          title="Dados do Credenciado"
          subtitle="Informações de identificação"
          icon={Building2}
        >
          <div className="grid gap-5 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="tipo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={(valor) => {
                      field.onChange(valor);
                      form.setValue('cpf_cnpj', '');
                    }}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {OPCOES_DE_TIPO.map((opcao) => (
                        <SelectItem key={opcao.value} value={opcao.value}>
                          {opcao.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cpf_cnpj"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tipo === 'PF' ? 'CPF' : 'CNPJ'}</FormLabel>
                  <FormControl>
                    <Input
                      value={tipo === 'PF' ? mascaraCpf(field.value) : mascaraCnpj(field.value)}
                      onChange={(e) => field.onChange(somenteDigitos(e.target.value))}
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={tipo === 'PF' ? 14 : 18}
                      placeholder={tipo === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                      className="font-mono"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome / Razão Social</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={200} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="nome_fantasia"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome Fantasia</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={200} autoComplete="off" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="termo_permissionario"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Termo Permissionário (TP)</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={50} placeholder="Nº do TP" autoComplete="off" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      maxLength={255}
                      placeholder="email@exemplo.com"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="telefone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <Input
                      value={mascaraTelefone(field.value)}
                      onChange={(e) => field.onChange(somenteDigitos(e.target.value))}
                      maxLength={15}
                      placeholder="(00) 00000-0000"
                      autoComplete="off"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="telefone2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone 2</FormLabel>
                  <FormControl>
                    <Input
                      value={mascaraTelefone(field.value)}
                      onChange={(e) => field.onChange(somenteDigitos(e.target.value))}
                      maxLength={15}
                      placeholder="(00) 00000-0000"
                      autoComplete="off"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection title="Endereço" subtitle="Localização do credenciado" icon={MapPin}>
          <div className="grid gap-5 sm:grid-cols-6">
            <FormField
              control={form.control}
              name="cep"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>CEP</FormLabel>
                  <FormControl>
                    <Input
                      value={mascaraCep(field.value)}
                      onChange={(e) => field.onChange(somenteDigitos(e.target.value))}
                      onBlur={() => void aoSairDoCep(field.value ?? '')}
                      maxLength={9}
                      placeholder="00000-000"
                      className="font-mono"
                      autoComplete="off"
                      aria-invalid={!!erroDoCep}
                    />
                  </FormControl>
                  {erroDoCep && (
                    <p className="text-destructive text-sm" role="alert">
                      {erroDoCep}
                    </p>
                  )}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="endereco"
              render={({ field }) => (
                <FormItem className="sm:col-span-4">
                  <FormLabel>Endereço</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={300}
                      disabled={carregandoCep}
                      placeholder={carregandoCep ? 'Buscando…' : undefined}
                      autoComplete="off"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="numero"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>Número</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={20} placeholder="Nº" autoComplete="off" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="complemento"
              render={({ field }) => (
                <FormItem className="sm:col-span-4">
                  <FormLabel>Complemento</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={100}
                      placeholder="Apto, bloco…"
                      autoComplete="off"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bairro"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>Bairro</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={100} disabled={carregandoCep} autoComplete="off" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cidade"
              render={({ field }) => (
                <FormItem className="sm:col-span-3">
                  <FormLabel>Cidade</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={100} disabled={carregandoCep} autoComplete="off" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="uf"
              render={({ field }) => (
                <FormItem className="sm:col-span-1">
                  <FormLabel>UF</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={2}
                      placeholder="SP"
                      disabled={carregandoCep}
                      className="uppercase"
                      autoComplete="off"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection title="Dados Bancários" subtitle="Informações para pagamento" icon={Landmark}>
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="conta_banco"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Banco</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={50}
                      placeholder="Ex: Banco do Brasil"
                      autoComplete="off"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="conta_agencia"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Agência</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={20}
                      placeholder="0000-0"
                      className="font-mono"
                      autoComplete="off"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="conta_numero"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Conta</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={20}
                      placeholder="00000-0"
                      className="font-mono"
                      autoComplete="off"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="conta_operacao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Operação</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={10} placeholder="001" autoComplete="off" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          {tipo !== 'PF' && (
            <FormField
              control={form.control}
              name="regime_tributario"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Regime Tributário</FormLabel>
                  <Select value={field.value || ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o regime…" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {REGIMES_TRIBUTARIOS.map((regime) => (
                        <SelectItem key={regime} value={regime}>
                          {ROTULO_DO_REGIME[regime]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          )}
        </FormSection>

        <FormSection title="Observações" icon={MessageSquare}>
          <FormField
            control={form.control}
            name="observacoes"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    {...field}
                    maxLength={1000}
                    rows={3}
                    placeholder="Informações adicionais sobre o credenciado…"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </FormSection>

        <RodapeDoFormularioEmModal formId={formId} aoCancelar={onCancelar} inline={rodapeInline} />
      </form>
    </Form>
  );
}

export default CredenciadoFormulario;
